import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { plants, areas } from '../data/mockData';
import { toast } from 'sonner';

const AppContext = createContext(null);

// Role definitions
const rolesConfig = [
  { 
    id: 'PLANT_HEAD', 
    name: 'Plant Head / Head Manufacturing', 
    permissions: ['all'],
    navAccess: ['all']
  },
  { 
    id: 'QA_HEAD', 
    name: 'QA Head', 
    permissions: ['all'],
    navAccess: ['all']
  },
  { 
    id: 'QA_OFFICER', 
    name: 'QA Officer', 
    permissions: ['execute_tasks', 'approve_tasks', 'investigate_deviations'],
    navAccess: ['/', '/tasks', '/checklists', '/requirements', '/deviations', '/capa', '/change-control', '/complaints', '/audits', '/assets', '/materials', '/documents', '/pqr', '/audit-packs', '/integrations']
  },
  { 
    id: 'PROD_SUP', 
    name: 'Production Supervisor', 
    permissions: ['execute_tasks', 'submit_issues', 'approve_tasks'],
    navAccess: ['/', '/tasks', '/checklists', '/deviations', '/capa', '/change-control', '/documents', '/assets']
  },
  { 
    id: 'QC_ANALYST', 
    name: 'QC Analyst', 
    permissions: ['execute_tasks', 'lab_tests'],
    navAccess: ['/', '/tasks', '/checklists', '/deviations', '/capa', '/documents', '/assets', '/materials']
  },
  { 
    id: 'ENGINEERING_HEAD', 
    name: 'Engineering Head', 
    permissions: ['execute_tasks', 'equipment_mgmt', 'calibration'],
    navAccess: ['/', '/tasks', '/checklists', '/assets', '/deviations', '/capa', '/change-control', '/documents']
  },
  { 
    id: 'ENGINEERING', 
    name: 'Engineering / Utilities', 
    permissions: ['execute_tasks', 'equipment_mgmt', 'calibration'],
    navAccess: ['/', '/tasks', '/checklists', '/assets', '/deviations', '/capa', '/change-control', '/documents']
  },
  { 
    id: 'INT_AUDITOR', 
    name: 'Internal Auditor / Compliance', 
    permissions: ['execute_audits', 'view_all'],
    navAccess: ['/', '/audits', '/audit-packs', '/requirements', '/deviations', '/capa', '/change-control', '/documents', '/pqr']
  },
  { 
    id: 'EXT_AUDITOR', 
    name: 'External Auditor (Read-only)', 
    permissions: ['view_only'],
    navAccess: ['all'] // Can view all, but read-only
  }
];

// Get initial state from localStorage or defaults
const getInitialState = () => {
  try {
    const savedRole = localStorage.getItem('smcp_role');
    const savedPlant = localStorage.getItem('smcp_plant');
    const savedArea = localStorage.getItem('smcp_area');
    const savedTimeScope = localStorage.getItem('smcp_timeScope');
    
    return {
      plantId: savedPlant || 'PLANT-A',
      areaId: savedArea || 'AREA-OSD',
      roleId: savedRole || 'QA_HEAD',
      timeScope: savedTimeScope || '30days'
    };
  } catch (e) {
    console.warn('Failed to load initial state from localStorage:', e);
    return {
      plantId: 'PLANT-A',
      areaId: 'AREA-OSD',
      roleId: 'QA_HEAD',
      timeScope: '30days'
    };
  }
};

export const AppProvider = ({ children }) => {
  const initialState = getInitialState();
  
  const [currentPlant, setCurrentPlant] = useState(() => 
    plants.find(p => p.id === initialState.plantId) || plants[0]
  );
  const [currentArea, setCurrentArea] = useState(() => 
    areas.find(a => a.id === initialState.areaId) || areas[0]
  );
  const [currentRole, setCurrentRole] = useState(() => 
    rolesConfig.find(r => r.id === initialState.roleId) || rolesConfig[0]
  );
  const [timeScope, setTimeScopeState] = useState(initialState.timeScope);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Save to localStorage when state changes
  useEffect(() => {
    try {
      localStorage.setItem('smcp_plant', currentPlant.id);
      localStorage.setItem('smcp_area', currentArea.id);
      localStorage.setItem('smcp_role', currentRole.id);
      localStorage.setItem('smcp_timeScope', timeScope);
    } catch (e) {
      console.warn('Failed to save to localStorage:', e);
    }
  }, [currentPlant.id, currentArea.id, currentRole.id, timeScope]);

  const switchPlant = useCallback((plantId) => {
    const plant = plants.find(p => p.id === plantId);
    if (plant) {
      setCurrentPlant(plant);
      // Reset area to first area of new plant
      const plantAreas = areas.filter(a => a.plantId === plantId);
      if (plantAreas.length > 0) {
        setCurrentArea(plantAreas[0]);
      }
    }
  }, []);

  const switchArea = useCallback((areaId) => {
    const area = areas.find(a => a.id === areaId);
    if (area) setCurrentArea(area);
  }, []);

  const switchRole = useCallback((roleId) => {
    const role = rolesConfig.find(r => r.id === roleId);
    if (role) {
      setCurrentRole(role);
      toast.success(`Viewing as: ${role.name}`, {
        description: role.id === 'EXT_AUDITOR' ? 'Read-only access enabled' : 'Full access enabled'
      });
      setShouldRedirect(true);
    }
  }, []);

  const setTimeScope = useCallback((scope) => {
    setTimeScopeState(scope);
  }, []);

  const hasPermission = useCallback((permission) => {
    if (currentRole.permissions.includes('all')) return true;
    if (currentRole.permissions.includes('view_only')) {
      return permission === 'view' || permission === 'view_only';
    }
    return currentRole.permissions.includes(permission);
  }, [currentRole]);

  const canAccessRoute = useCallback((route) => {
    if (currentRole.navAccess.includes('all')) return true;
    
    // Check if the route or any parent route is in navAccess
    return currentRole.navAccess.some(allowedRoute => {
      if (route === allowedRoute) return true;
      if (route.startsWith(allowedRoute + '/')) return true;
      return false;
    });
  }, [currentRole]);

  const isReadOnly = currentRole.id === 'EXT_AUDITOR';
  const canEdit = !isReadOnly;

  const value = {
    currentPlant,
    currentArea,
    currentRole,
    timeScope,
    switchPlant,
    switchArea,
    switchRole,
    setTimeScope,
    hasPermission,
    canAccessRoute,
    canEdit,
    isReadOnly,
    plants,
    areas: areas.filter(a => a.plantId === currentPlant.id),
    allAreas: areas,
    roles: rolesConfig,
    shouldRedirect,
    setShouldRedirect
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
